import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Shield, Building2, KeyRound, Eye, EyeOff, Calendar } from 'lucide-react';
import TwoFactorAuthModal from './TwoFactorAuthModal';
import ChangePasswordModal from './ChangePasswordModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserProfile({ user, onClose }) {
  const [show2FA, setShow2FA] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="user-profile-modal">
      <div className="bg-white w-full max-w-md rounded-xl border-2 border-gray-200 shadow-xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex justify-between items-center z-10">
          <h2 className="font-manrope font-bold text-xl text-text-primary">Profile</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary text-2xl leading-none" data-testid="btn-close-profile">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">
              {user.full_name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-text-primary text-lg">{user.full_name}</p>
              <p className="text-text-muted text-sm capitalize">{user.role}</p>
            </div>
          </div>

          {/* Info */}
          <div className="space-y-3 bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Mail size={16} className="text-text-muted flex-shrink-0" />
              <span className="text-sm text-text-primary truncate">{user.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Shield size={16} className="text-text-muted flex-shrink-0" />
              <span className="text-sm text-text-primary capitalize">{user.role}</span>
            </div>
            <div className="flex items-center gap-3">
              <Building2 size={16} className="text-text-muted flex-shrink-0" />
              <span className="text-sm text-text-primary">{user.org_name}</span>
            </div>
            {user.created_at && (
              <div className="flex items-center gap-3">
                <Calendar size={16} className="text-text-muted flex-shrink-0" />
                <span className="text-sm text-text-primary">Joined {new Date(user.created_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>

          {/* 2FA Status */}
          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-primary">Two-Factor Auth</p>
              <p className="text-xs text-text-muted">{user.two_factor_enabled ? 'Enabled' : 'Not enabled'}</p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${user.two_factor_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'}`}>
              {user.two_factor_enabled ? 'ON' : 'OFF'}
            </span>
          </div>

          {/* Actions */}
          <div className="space-y-2 pt-2">
            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-primary transition-colors text-left"
              data-testid="btn-change-password"
            >
              <KeyRound size={18} className="text-primary" />
              <span className="text-sm font-medium text-text-primary">Change Password</span>
            </button>
            <button
              onClick={() => setShow2FA(true)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-primary transition-colors text-left"
              data-testid="btn-setup-2fa"
            >
              <Shield size={18} className="text-primary" />
              <span className="text-sm font-medium text-text-primary">
                {user.two_factor_enabled ? 'Manage 2FA' : 'Enable 2FA'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {show2FA && <TwoFactorAuthModal onClose={() => setShow2FA(false)} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}
